import React from 'react'

import { TextClamp } from '../components/text/TextClamp'
import { EditableText } from '../slides/EditableText'
import type { EditableSlideProps } from '../slides/contracts'
import type { ActionCardData } from '../slides/renderer-internal-types'

const DARK_GRAY = 'var(--slide-text-secondary)'
const CARD_BACKGROUNDS = [
  'var(--ds-exec-scr-card-a)',
  'var(--ds-exec-scr-card-b)',
  'var(--ds-exec-scr-card-c)',
]

function ActionRecommendationCard(props: { action: ActionCardData; index: number } & EditableSlideProps) {
  const { action, index, editable, onEdit, renderMode } = props
  return (
    <article
      data-testid="recommendations-card"
      style={{
        background: CARD_BACKGROUNDS[index % CARD_BACKGROUNDS.length],
        borderRadius: 12,
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
        height: '100%',
        minHeight: 283,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
        }}
      >
        {editable && onEdit ? (
          <EditableText
            value={action.title}
            field={`actions.${index}.title`}
            editable
            onEdit={onEdit}
            tag="span"
            style={{ fontSize: 18, fontWeight: 600, color: 'var(--slide-text-primary)' }}
          />
        ) : (
          <TextClamp renderMode={renderMode} minLines={1} maxLines={1} lineHeight="1.4" style={{ fontSize: 18, fontWeight: 600, color: 'var(--slide-text-primary)' }}>
            {action.title}
          </TextClamp>
        )}
      </div>
      {editable && onEdit ? (
        <EditableText
          value={action.body}
          field={`actions.${index}.body`}
          editable
          onEdit={onEdit}
          tag="p"
          multiline
          style={{ fontSize: 16, color: DARK_GRAY, lineHeight: '1.6' }}
        />
      ) : (
        <TextClamp renderMode={renderMode} minLines={5} maxLines={10} lineHeight="1.6" style={{ fontSize: 16, color: DARK_GRAY }}>
          {action.body}
        </TextClamp>
      )}
    </article>
  )
}

/**
 * The action-cards slot (plots plan Stage 2, AA): the recommendations' action cards,
 * up to three across (`repeat(n, 1fr)`, gap 24), filling the space left below the
 * intro. Each card: a one-line title, then a 5 to 10 line body. The fills step the
 * same brand wash as the exec-summary cards.
 */
export interface SlotActionCardsProps extends EditableSlideProps {
  actions: Array<ActionCardData>
}

export function SlotActionCards(props: SlotActionCardsProps) {
  const { actions, editable, onEdit, renderMode } = props
  const actionColumnCount = Math.max(1, Math.min(3, actions.length))
  return (
    <div data-testid="recommendations-cards" style={{ display: 'grid', gridTemplateColumns: `repeat(${actionColumnCount}, minmax(0, 1fr))`, gap: 24, flex: 1 }}>
      {actions.map((action, index) => (
        <ActionRecommendationCard key={`${action.stepNumber}-${action.title}`} action={action} index={index} editable={editable} onEdit={onEdit} renderMode={renderMode} />
      ))}
    </div>
  )
}
